import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  monitoringCountersService,
  mapMonitoringCounters,
  type LC_MonitoringFiltersIn,
  type MonitoringCounters,
} from '@/api/domains/attendance/monitoring';
import { useAbortController } from '@/hooks/ui/useAbortController';
import { useUI } from '@/hooks/ui/useUI';


export interface UseMonitoringCountersResult {
  counters: MonitoringCounters | null;
  /** `true` solo mientras se espera la primera respuesta. */
  isInitialLoading: boolean;
  /** `true` en los refrescos posteriores, que conservan los valores a la vista. */
  isRefreshing: boolean;
  refetch: () => void;
}

// Contadores del Monitoreo de Asistencia (activos, ausentes, justificados, presentes) para los
// filtros aplicados.
export function useMonitoringCounters(
  appliedFilters: LC_MonitoringFiltersIn | null,
  refreshToken = 0,
  onRefreshCompleted?: () => void,
): UseMonitoringCountersResult {
  const { t: translate } = useTranslation();
  const { showApiError } = useUI();
  const { createSignal } = useAbortController();

  const [counters, setCounters] = useState<MonitoringCounters | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState<boolean>(false);

  // Ref para que el callback no forme parte de las dependencias del fetch.
  const onRefreshCompletedRef = useRef(onRefreshCompleted);
  useEffect(() => {
    onRefreshCompletedRef.current = onRefreshCompleted;
  }, [onRefreshCompleted]);

  const fetchCounters = useCallback(async (): Promise<void> => {
    if (appliedFilters === null) {
      return;
    }

    setLoading(true);
    const signal = createSignal();
    const response = await monitoringCountersService.get(appliedFilters, signal);

    if (response.error?.code === 'ABORTED') {
      return;
    }

    if (!response.success || !response.data) {
      showApiError(response, translate('attendance:monitoring.counters.loadError'));
      setCounters(null);
      setHasLoadedOnce(true);
      setLoading(false);
      return;
    }

    setCounters(mapMonitoringCounters(response.data));
    setHasLoadedOnce(true);
    setLoading(false);
    onRefreshCompletedRef.current?.();
  }, [appliedFilters, createSignal, showApiError, translate]);

  useEffect(() => {
    void fetchCounters();
  }, [fetchCounters]);

  // Solo reconsulta cuando el token cambia de valor; el inicial no agrega un fetch al del montaje.
  const previousRefreshTokenRef = useRef(refreshToken);
  useEffect(() => {
    if (previousRefreshTokenRef.current === refreshToken) return;
    previousRefreshTokenRef.current = refreshToken;
    void fetchCounters();
  }, [refreshToken, fetchCounters]);

  const refetch = useCallback((): void => {
    void fetchCounters();
  }, [fetchCounters]);

  return {
    counters,
    isInitialLoading: loading && !hasLoadedOnce,
    isRefreshing: loading && hasLoadedOnce,
    refetch,
  };
}

export default useMonitoringCounters;
